
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, PlusCircle, FileDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

// Mock data
const adjustmentsData = [
  {
    id: 1,
    date: "2025-05-11T14:30:00",
    productName: "Organic Brown Rice 1kg",
    quantity: 5,
    type: "Stock In",
    reason: "Delivery",
    doneBy: "Ahmad",
    notes: "Weekly restock",
    approved: true
  },
  {
    id: 2,
    date: "2025-05-10T11:45:00",
    productName: "Fresh Milk 1L",
    quantity: -2,
    type: "Stock Out",
    reason: "Damage",
    doneBy: "Sarah",
    notes: "Packaging damaged during handling",
    approved: true
  },
  {
    id: 3,
    date: "2025-05-09T15:20:00",
    productName: "Yogurt 500g",
    quantity: -1,
    type: "Stock Out",
    reason: "Expired",
    doneBy: "Ahmad",
    notes: "",
    approved: true
  },
  {
    id: 4,
    date: "2025-05-09T10:10:00",
    productName: "Chicken Breast (Fresh)",
    quantity: -3,
    type: "Stock Out",
    reason: "Correction",
    doneBy: "Manager",
    notes: "Count error from previous day",
    approved: false
  },
  {
    id: 5,
    date: "2025-05-08T16:30:00",
    productName: "Instant Coffee 200g",
    quantity: 10,
    type: "Stock In",
    reason: "Return",
    doneBy: "Sarah",
    notes: "Customer return - unopened",
    approved: true
  }
];

const StockAdjustmentsTab = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [approvals, setApprovals] = useState<number[]>(
    adjustmentsData.filter(item => item.approved).map(item => item.id)
  );
  
  const filteredAdjustmentsData = adjustmentsData.filter(item =>
    item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.doneBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.reason.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const toggleApproval = (id: number) => {
    if (approvals.includes(id)) {
      setApprovals(approvals.filter(itemId => itemId !== id));
    } else {
      setApprovals([...approvals, id]);
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Stock Adjustments</h2>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search adjustments..."
              className="pl-8 w-[250px]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button variant="outline">
            <FileDown className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button>
            <PlusCircle className="mr-2 h-4 w-4" />
            New Adjustment
          </Button>
        </div>
      </div>
      
      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Product Name</TableHead>
              <TableHead className="text-center">Quantity Adjusted</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Done By</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead className="text-center">Approved</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAdjustmentsData.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{formatDate(item.date)}</TableCell>
                <TableCell className="font-medium">{item.productName}</TableCell>
                <TableCell className="text-center">
                  <span className={item.quantity < 0 ? "text-red-600" : "text-green-600"}>
                    {item.quantity > 0 ? `+${item.quantity}` : item.quantity}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={item.type === "Stock In" ? "text-green-600" : "text-red-600"}>
                    {item.type}
                  </span>
                </TableCell>
                <TableCell>{item.reason}</TableCell>
                <TableCell>{item.doneBy}</TableCell>
                <TableCell className="max-w-[200px] truncate">{item.notes}</TableCell>
                <TableCell className="text-center">
                  <Checkbox 
                    checked={approvals.includes(item.id)}
                    onCheckedChange={() => toggleApproval(item.id)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default StockAdjustmentsTab;
